import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Lock, Crown, ArrowRight, RefreshCw, Gift, Users, Flame,
  CheckCircle2, Copy, Share2, Clock, Zap, Package,
} from "lucide-react";
import { trackEvent } from "@/lib/posthog";

/**
 * Earn Credits Hub — unified rewarded UX with 3 paths:
 *   1. Quick Wins (always works) — referral, profile bonus, streak milestones
 *   2. Surveys (CPX Research) — variable fill, real money
 *   3. Offers (offerwall placeholder) — coming soon
 *
 * Shared 20-credits/week cap across all paths protects subscription
 * revenue. Above the cap, the modal nudges toward Upgrade.
 */
export default function EarnCreditsHub({ open, onOpenChange, onRewarded, onUpgrade }) {
  const [tab, setTab] = useState("quick"); // 'quick' | 'surveys' | 'offers'
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(false);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [iframeError, setIframeError] = useState(null);
  const startBalanceRef = useRef(null);
  const pollRef = useRef(null);

  const refresh = async () => {
    try {
      const [h, bal] = await Promise.all([
        api.get("/earn/status"),
        api.get("/credits/balance"),
      ]);
      setHub(h.data);
      startBalanceRef.current = bal.data?.credits ?? 0;
      return { hub: h.data, balance: bal.data?.credits };
    } catch {
      setHub(null);
      return null;
    }
  };

  useEffect(() => {
    if (!open) {
      setIframeUrl(null);
      setIframeError(null);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    setLoading(true);
    refresh().finally(() => setLoading(false));
    trackEvent("earn_hub_opened");
  }, [open]);

  // Poll wallet every 4s while modal open. Pick up CPX postback completions.
  useEffect(() => {
    if (!open) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/credits/balance");
        const start = startBalanceRef.current ?? 0;
        if (typeof data?.credits === "number" && data.credits > start) {
          const earned = data.credits - start;
          startBalanceRef.current = data.credits;
          toast.success(`You earned ${earned} credit${earned === 1 ? "" : "s"}!`);
          onRewarded?.(data.credits);
          // Refresh hub to reflect new weekly cap usage.
          api.get("/earn/status").then(({ data: h }) => setHub(h)).catch(() => {});
        }
      } catch {}
    }, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, onRewarded]);

  // When user switches to Surveys tab, lazy-load the CPX iframe.
  useEffect(() => {
    if (!open || tab !== "surveys") return;
    if (iframeUrl || iframeError) return;
    if (!hub?.surveys?.enabled) return;
    if ((hub?.surveys?.remaining_today ?? 0) <= 0) return;
    if ((hub?.weekly_remaining ?? 0) <= 0) return;
    api.get("/cpx/session-url")
      .then(({ data }) => setIframeUrl(data?.iframe_url || null))
      .catch((e) => setIframeError(e?.response?.data?.detail || "Could not load surveys"));
  }, [open, tab, hub, iframeUrl, iframeError]);

  const triggerUpgrade = () => { onOpenChange(false); onUpgrade?.(); };
  const capHit = hub && (hub.weekly_remaining ?? 0) <= 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass border-white/10 max-w-3xl w-[calc(100vw-2rem)] p-0 overflow-hidden"
        data-testid="earn-credits-hub"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/5">
          <DialogTitle className="font-display flex items-center gap-2 text-base">
            <Gift className="text-[#00FF9D]" size={18} /> Earn Credits
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Pick your path · credits land instantly · max 20 / week from rewards
          </DialogDescription>
        </DialogHeader>

        {/* Weekly cap meter */}
        {hub && (
          <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between gap-3 text-[11px]">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-white/55">Weekly earnings</span>
                <span className="font-mono-oak text-white/70" data-testid="earn-weekly-meter">
                  {hub.weekly_earned} / {hub.weekly_cap}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#00FF9D] to-[#0044FF] transition-all"
                  style={{ width: `${Math.min(100, (hub.weekly_earned / hub.weekly_cap) * 100)}%` }}
                />
              </div>
            </div>
            {capHit && (
              <button
                onClick={triggerUpgrade}
                className="text-[10px] font-display font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full btn-primary"
                data-testid="btn-cap-upgrade"
              >
                Upgrade for unlimited
              </button>
            )}
          </div>
        )}

        {/* Tab switcher */}
        <div className="px-5 pt-3 flex gap-1 border-b border-white/5">
          <TabButton active={tab === "quick"} onClick={() => setTab("quick")} icon={Zap} label="Quick Wins" testid="tab-quick" />
          <TabButton active={tab === "surveys"} onClick={() => setTab("surveys")} icon={Sparkles} label="Surveys" testid="tab-surveys" />
          <TabButton active={tab === "offers"} onClick={() => setTab("offers")} icon={Package} label="Offers" testid="tab-offers" />
        </div>

        <div className="max-h-[65vh] overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
          {loading && !hub ? (
            <div className="p-10 grid place-items-center" data-testid="earn-loading">
              <RefreshCw className="animate-spin text-white/45" size={24} />
            </div>
          ) : capHit ? (
            <CapReachedView onUpgrade={triggerUpgrade} onClose={() => onOpenChange(false)} />
          ) : tab === "quick" ? (
            <QuickWinsTab hub={hub} onClaimed={refresh} onRewarded={onRewarded} />
          ) : tab === "surveys" ? (
            <SurveysTab hub={hub} iframeUrl={iframeUrl} iframeError={iframeError}
              onSwitchTab={setTab} onUpgrade={triggerUpgrade}
              onRetryIframe={() => { setIframeUrl(null); setIframeError(null); }} />
          ) : (
            <OffersTab hub={hub} onSwitchTab={setTab} onRewarded={onRewarded} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-xs font-display flex items-center gap-1.5 border-b-2 transition ${
        active
          ? "text-white border-[#00FF9D]"
          : "text-white/55 hover:text-white border-transparent"
      }`}
      data-testid={testid}
    >
      <Icon size={13} /> {label}
    </button>
  );
}

// --- Quick Wins Tab ---------------------------------------------------------

function QuickWinsTab({ hub, onClaimed, onRewarded }) {
  if (!hub) return null;
  const { quick_wins } = hub;
  return (
    <div className="p-5 space-y-3" data-testid="quick-wins-tab">
      <ProfileBonusCard data={quick_wins.profile_bonus} onClaimed={onClaimed} onRewarded={onRewarded} />
      <StreakCard data={quick_wins.streak} onClaimed={onClaimed} onRewarded={onRewarded} />
      <ReferralCard data={quick_wins.referral} />
    </div>
  );
}

function ProfileBonusCard({ data, onClaimed, onRewarded }) {
  const [busy, setBusy] = useState(false);
  const claim = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/earn/profile-bonus");
      if (r.status === "ok" && r.granted > 0) {
        toast.success(`+${r.granted} credits!`);
        onRewarded?.(r.credits);
      } else if (r.status === "already_claimed") {
        toast.info("Already claimed");
      }
      await onClaimed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't claim");
    } finally {
      setBusy(false);
    }
  };
  const claimed = data.claimed;
  const eligible = data.eligible;
  return (
    <div className="glass p-4 border border-white/10 flex items-start gap-3" data-testid="card-profile-bonus">
      <div className="h-10 w-10 grid place-items-center rounded-xl bg-[#0044FF]/15 border border-[#0044FF]/30">
        <Sparkles className="text-[#8aa8ff]" size={18} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-display text-sm">Complete your profile</div>
          <div className="text-[#00FF9D] font-display text-sm font-semibold">+{data.reward}</div>
        </div>
        <div className="text-white/50 text-xs mt-0.5">
          One-time bonus when your domain, seniority, and goal are set.
        </div>
      </div>
      {claimed ? (
        <button disabled className="px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider border border-white/10 text-white/40 flex items-center gap-1" data-testid="btn-profile-claimed">
          <CheckCircle2 size={11} /> Claimed
        </button>
      ) : eligible ? (
        <button onClick={claim} disabled={busy}
          className="px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider btn-primary font-display font-semibold disabled:opacity-50"
          data-testid="btn-profile-claim">
          {busy ? "..." : "Claim"}
        </button>
      ) : (
        <a href="/settings" className="px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider border border-white/15 hover:border-white/30 text-white/70 hover:text-white" data-testid="btn-profile-setup">
          Set up
        </a>
      )}
    </div>
  );
}

function StreakCard({ data, onClaimed, onRewarded }) {
  const [busy, setBusy] = useState(null);
  const claim = async (days) => {
    setBusy(days);
    try {
      const { data: r } = await api.post(`/earn/streak-milestone/${days}`);
      if (r.status === "ok" && r.granted > 0) {
        toast.success(`+${r.granted} credits!`);
        onRewarded?.(r.credits);
      } else if (r.status === "already_claimed") {
        toast.info("Already claimed");
      }
      await onClaimed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't claim");
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="glass p-4 border border-white/10" data-testid="card-streak">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 grid place-items-center rounded-xl bg-[#FF7B00]/15 border border-[#FF7B00]/30">
          <Flame className="text-[#ffb067]" size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-display text-sm">Daily streak milestones</div>
            <div className="text-white/55 text-xs font-mono-oak" data-testid="streak-current">
              {data.current_streak}d streak
            </div>
          </div>
          <div className="text-white/50 text-xs mt-0.5">
            Mark a Micro-Chunker task done each day. Bonuses unlock at 3, 7, and 14 days.
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        {data.tiers.map((t) => (
          <button
            key={t.days}
            disabled={!t.unlocked || t.claimed || busy === t.days}
            onClick={() => claim(t.days)}
            className={`p-3 rounded-xl text-center border transition ${
              t.claimed
                ? "border-white/10 bg-white/[0.02] text-white/40"
                : t.unlocked
                ? "border-[#FF7B00]/40 bg-[#FF7B00]/[0.08] text-[#ffb067] hover:bg-[#FF7B00]/[0.16]"
                : "border-white/10 bg-white/[0.02] text-white/30"
            }`}
            data-testid={`streak-tier-${t.days}`}
          >
            <div className="font-display text-lg font-semibold">+{t.reward}</div>
            <div className="text-[10px] uppercase tracking-wider mt-0.5">
              {t.claimed ? "Claimed" : t.unlocked ? `Claim (${t.days}d)` : `Day ${t.days}`}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ReferralCard({ data }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(data.share_url).then(() => {
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
      trackEvent("referral_link_copied", { code: data.code });
    }).catch(() => toast.error("Copy failed"));
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Oak AI Campus",
          text: `Join me on Oak AI Campus — AI-guided learning paths. Sign up with my link and we both get bonus credits.`,
          url: data.share_url,
        });
        trackEvent("referral_link_shared", { code: data.code });
      } catch {}
    } else {
      copy();
    }
  };
  return (
    <div className="glass p-4 border border-white/10" data-testid="card-referral">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 grid place-items-center rounded-xl bg-[#00FF9D]/15 border border-[#00FF9D]/30">
          <Users className="text-[#00FF9D]" size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-display text-sm">Refer a friend</div>
            <div className="text-[#00FF9D] font-display text-sm font-semibold">+{data.reward_referrer} you · +{data.reward_invitee} them</div>
          </div>
          <div className="text-white/50 text-xs mt-0.5">
            Share your link. Both of you earn credits when they sign up.
            {data.count > 0 && <span className="text-[#00FF9D]"> · {data.count} signed up so far</span>}
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <div className="flex-1 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-white/70 truncate font-mono-oak" data-testid="referral-link">
          {data.share_url}
        </div>
        <button onClick={copy} className="px-3 py-2 rounded-lg border border-white/15 hover:border-white/30 text-xs flex items-center gap-1.5 transition" data-testid="btn-referral-copy">
          {copied ? <CheckCircle2 size={13} className="text-[#00FF9D]" /> : <Copy size={13} />}
          {copied ? "Copied" : "Copy"}
        </button>
        <button onClick={share} className="px-3 py-2 rounded-lg btn-primary font-display font-semibold text-xs flex items-center gap-1.5" data-testid="btn-referral-share">
          <Share2 size={13} /> Share
        </button>
      </div>
    </div>
  );
}

// --- Surveys Tab ------------------------------------------------------------

function SurveysTab({ hub, iframeUrl, iframeError, onSwitchTab, onUpgrade, onRetryIframe }) {
  if (!hub) return null;
  const s = hub.surveys;
  const dailyHit = (s.remaining_today ?? 0) <= 0;

  if (!s.enabled) {
    return (
      <EmptyState
        icon={Clock} title="Surveys unavailable"
        body="Survey wall is offline right now. Try Quick Wins or Offers."
        primary={{ label: "View Quick Wins", onClick: () => onSwitchTab("quick") }}
      />
    );
  }
  if (dailyHit) {
    return (
      <EmptyState
        icon={Lock} title="Daily survey limit reached"
        body={`You've completed ${s.daily_cap} surveys today. Come back tomorrow, or earn from Quick Wins.`}
        primary={{ label: "Try Quick Wins", onClick: () => onSwitchTab("quick") }}
      />
    );
  }
  if (iframeError) {
    return (
      <div className="p-5 space-y-4" data-testid="surveys-error">
        <div className="rounded-xl bg-rose-500/10 border border-rose-500/20 p-4 text-sm text-rose-200">
          {iframeError}
        </div>
        <div className="flex gap-2">
          <button onClick={onRetryIframe} className="flex-1 py-2.5 rounded-xl border border-white/15 text-sm flex items-center justify-center gap-2" data-testid="btn-surveys-retry">
            <RefreshCw size={14} /> Retry
          </button>
          <button onClick={() => onSwitchTab("quick")} className="flex-1 py-2.5 rounded-xl btn-primary text-sm font-display font-semibold" data-testid="btn-surveys-fallback">
            Try Quick Wins
          </button>
        </div>
      </div>
    );
  }
  if (!iframeUrl) {
    return (
      <div className="p-10 grid place-items-center" data-testid="surveys-loading">
        <RefreshCw className="animate-spin text-white/45" size={24} />
        <div className="mt-3 text-sm text-white/55">Loading survey wall…</div>
      </div>
    );
  }
  return (
    <div data-testid="surveys-iframe-container">
      <iframe
        title="CPX Research Survey Wall"
        src={iframeUrl}
        className="w-full block"
        style={{ height: "min(60vh, 640px)", border: "none", background: "#0B0E14" }}
        allow="clipboard-write; fullscreen"
        data-testid="cpx-iframe"
      />
      <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between text-[11px] text-white/45">
        <span>Powered by CPX Research · Earnings credit instantly</span>
        <span className="font-mono-oak">
          {s.completed_today} / {s.daily_cap} today
        </span>
      </div>
    </div>
  );
}

// --- Offers Tab (Adsterra Smartlink — direct link hyperlink only) -----------
//
// Re-enabled Feb 2026 per user request. SMARTLINK ONLY: we open the
// configured URL in a new tab and let the user dwell. We never inject
// Adsterra <script> tags into the page — that was the source of the
// pop-up/adult-content issue last time.

function OffersTab({ hub, onSwitchTab, onRewarded }) {
  const offers = hub?.offers;
  const enabled = !!offers?.enabled;
  const remaining = offers?.remaining_today ?? 0;
  const reward = offers?.reward ?? 1;
  const minDwell = offers?.min_view_seconds ?? 15;

  const [click, setClick] = useState(null); // {click_id, url}
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  // Clean up timer when tab unmounts.
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  if (!enabled) {
    return (
      <EmptyState
        icon={Package} title="Offers · Coming Soon"
        body={offers?.message || "Partnering with a verified-clean offerwall provider."}
        primary={{ label: "Earn now via Quick Wins", onClick: () => onSwitchTab("quick") }}
        secondary={{ label: "Or take a survey", onClick: () => onSwitchTab("surveys") }}
      />
    );
  }

  if (remaining <= 0) {
    return (
      <EmptyState
        icon={Lock} title="Daily sponsor limit reached"
        body={`You've used today's ${offers.daily_cap} sponsor visits. Come back tomorrow, or earn via Quick Wins.`}
        primary={{ label: "Try Quick Wins", onClick: () => onSwitchTab("quick") }}
      />
    );
  }

  const startVisit = async () => {
    setError(null);
    try {
      const { data } = await api.post("/earn/adsterra/start");
      setClick({ click_id: data.click_id, url: data.url });
      // Open in a new tab. noopener prevents the destination from
      // grabbing window.opener; noreferrer scrubs the referer header.
      window.open(data.url, "_blank", "noopener,noreferrer");
      setSecondsLeft(data.min_view_seconds ?? minDwell);
      trackEvent("offerwall_visit_started", { provider: "adsterra" });
      timerRef.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) { clearInterval(timerRef.current); timerRef.current = null; return 0; }
          return s - 1;
        });
      }, 1000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't start visit. Try again.");
    }
  };

  const claim = async () => {
    if (!click) return;
    setClaiming(true);
    setError(null);
    try {
      const { data } = await api.post("/earn/adsterra/claim", { click_id: click.click_id });
      if (data.granted > 0) {
        toast.success(`+${data.granted} credit${data.granted === 1 ? "" : "s"}!`);
        onRewarded?.(data.credits);
      } else if (data.status === "already_claimed") {
        toast.info("Already claimed");
      } else if (data.weekly_cap_hit) {
        toast.info("Weekly cap reached — upgrade for unlimited.");
      }
      setClick(null);
      setSecondsLeft(0);
      trackEvent("offerwall_visit_claimed", { provider: "adsterra", granted: data.granted });
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't claim — try again.");
    } finally {
      setClaiming(false);
    }
  };

  const dwellDone = click && secondsLeft <= 0;

  return (
    <div className="p-5 space-y-3" data-testid="offers-tab-adsterra">
      <div className="glass p-4 border border-white/10" data-testid="card-adsterra-smartlink">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 grid place-items-center rounded-xl bg-[#5D3FD3]/15 border border-[#5D3FD3]/30">
            <Package className="text-[#a48aff]" size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="font-display text-sm">Visit a sponsor</div>
              <div className="text-[#00FF9D] font-display text-sm font-semibold">+{reward}</div>
            </div>
            <div className="text-white/50 text-xs mt-0.5">
              Click the button, stay on the sponsor page for at least {minDwell}s in the new tab, then come back and claim.
            </div>
          </div>
          <div className="text-white/55 text-[11px] font-mono-oak whitespace-nowrap" data-testid="adsterra-remaining">
            {remaining} / {offers.daily_cap} today
          </div>
        </div>

        {error && (
          <div className="mt-3 rounded-lg bg-rose-500/10 border border-rose-500/20 px-3 py-2 text-xs text-rose-200" data-testid="adsterra-error">
            {error}
          </div>
        )}

        <div className="mt-3 flex gap-2">
          {!click ? (
            <button
              onClick={startVisit}
              className="flex-1 py-2.5 rounded-xl btn-primary font-display font-semibold text-sm flex items-center justify-center gap-2"
              data-testid="btn-adsterra-start"
            >
              <ArrowRight size={14} /> Open Sponsor
            </button>
          ) : !dwellDone ? (
            <button
              disabled
              className="flex-1 py-2.5 rounded-xl border border-white/10 bg-white/[0.02] text-white/55 text-sm flex items-center justify-center gap-2"
              data-testid="btn-adsterra-dwell"
            >
              <Clock size={14} /> Stay on sponsor… {secondsLeft}s
            </button>
          ) : (
            <button
              onClick={claim}
              disabled={claiming}
              className="flex-1 py-2.5 rounded-xl btn-primary font-display font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="btn-adsterra-claim"
            >
              {claiming ? "Claiming…" : <>Claim +{reward}<CheckCircle2 size={14} /></>}
            </button>
          )}
        </div>

        <div className="mt-3 text-[10px] text-white/35 leading-relaxed">
          Sponsored by third-party advertisers via Adsterra. We don't control the destination — please skip anything that feels off.
        </div>
      </div>
    </div>
  );
}

// --- Cap Reached View -------------------------------------------------------

function CapReachedView({ onUpgrade, onClose }) {
  return (
    <div className="p-5 space-y-5" data-testid="cap-reached">
      <div className="aspect-video rounded-xl bg-gradient-to-br from-[#FF7B00]/40 via-[#5D3FD3]/40 to-[#0044FF]/40 grid place-items-center border border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 sheen opacity-30" />
        <div className="text-center z-10 px-6">
          <Crown className="mx-auto text-[#ffb067] mb-2" size={32} />
          <div className="font-display text-2xl tracking-tight leading-tight">Weekly cap reached</div>
          <div className="text-white/65 text-sm mt-1">You've earned the max free credits this week. Upgrade for unlimited.</div>
        </div>
      </div>
      <button onClick={onUpgrade} className="w-full py-3 rounded-xl btn-primary font-semibold font-display flex items-center justify-center gap-2" data-testid="btn-cap-upgrade-cta">
        Upgrade Now <ArrowRight size={16} />
      </button>
      <button onClick={onClose} className="w-full text-xs text-white/40 hover:text-white/70 transition" data-testid="btn-cap-close">
        I'll come back next week
      </button>
    </div>
  );
}

// --- Empty State Helper -----------------------------------------------------

function EmptyState({ icon: Icon, title, body, primary, secondary }) {
  return (
    <div className="p-8 text-center" data-testid="earn-empty-state">
      <div className="mx-auto h-12 w-12 grid place-items-center rounded-xl bg-white/5 border border-white/10 mb-3">
        <Icon className="text-white/55" size={20} />
      </div>
      <div className="font-display text-lg">{title}</div>
      <div className="text-white/55 text-sm mt-1.5 max-w-md mx-auto">{body}</div>
      <div className="flex flex-col sm:flex-row gap-2 mt-5 max-w-sm mx-auto">
        {primary && (
          <button onClick={primary.onClick} className="flex-1 py-2.5 rounded-xl btn-primary font-display font-semibold text-sm">
            {primary.label}
          </button>
        )}
        {secondary && (
          <button onClick={secondary.onClick} className="flex-1 py-2.5 rounded-xl border border-white/15 hover:border-white/30 text-sm">
            {secondary.label}
          </button>
        )}
      </div>
    </div>
  );
}
